/**
 * SubscriptionModal — VEN-06: Crear / Editar Suscripción (Factura Recurrente)
 */
import { todayISO } from '../../../lib/utils'
import { useState, useEffect, useMemo } from 'react'
import DatePicker from '@/components/DatePicker'
import { Loader2, Plus, Trash2 } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'

/** Una línea de la suscripción, con su posición en el array. */
type FilaDeLinea = RecurringInvoiceLine & { idx: number }
import { Autocomplete } from '@/components/Autocomplete'
import { NumericInput } from '@/components/NumericInput'
import { formatMoneyForTenant, useTenantLocale } from '@/lib/locale'
import {
  useCustomers,
  useContracts,
  useCreateRecurringInvoice,
  useUpdateRecurringInvoice,
  type RecurringInvoice,
  type RecurringInvoiceLine,
  type ProductListItem,
} from '../hooks/useSales'
import { ProductAutocomplete } from './ProductAutocomplete'
import { useGlobalCustomFields } from '@/modules/config/hooks/useConfig'
import { usePaymentGateways, useCustomerTokens } from '@/modules/payments/hooks/usePayments'
import { Modal } from '@/components/ds/Modal'

interface InitialDocData {
  customer: string
  document_type: string
  lines: RecurringInvoiceLine[]
  notes: string
}

interface SubscriptionModalProps {
  subscription?: RecurringInvoice | null
  initialDoc?: InitialDocData | null
  onClose: () => void
}

const EMPTY_LINE: RecurringInvoiceLine = {
  sku: '', name: '', unit: 'UN', quantity: '1', unit_price: '0',
}

export function SubscriptionModal({ subscription, initialDoc, onClose }: SubscriptionModalProps) {
  const isEdit = !!subscription
  const locale = useTenantLocale()

  const [customerId, setCustomerId] = useState(subscription?.customer ?? initialDoc?.customer ?? '')
  const [contractId, setContractId] = useState(subscription?.contract ?? '')
  const [docType, setDocType] = useState<'FE' | 'FNA' | 'BE' | 'BNA'>(
    (subscription?.document_type ?? initialDoc?.document_type ?? 'FE') as 'FE' | 'FNA' | 'BE' | 'BNA'
  )
  const [frequency, setFrequency] = useState(subscription?.frequency ?? 'monthly')
  const [dayOfMonth, setDayOfMonth] = useState(String(subscription?.day_of_month ?? 1))
  const [nextDate, setNextDate] = useState(
    subscription?.next_generation_date ?? todayISO()
  )
  const [generateAs, setGenerateAs] = useState(subscription?.generate_as ?? 'draft')
  const [notes, setNotes] = useState(subscription?.notes ?? initialDoc?.notes ?? '')
  const [lines, setLines] = useState<RecurringInvoiceLine[]>(
    subscription?.lines?.length
      ? subscription.lines
      : (initialDoc?.lines?.length ? initialDoc.lines : [{ ...EMPTY_LINE }])
  )
  const [apiError, setApiError] = useState<string | null>(null)
  const [personalizados, setPersonalizados] = useState<Record<string, string>>(
    subscription?.personalizados ?? {}
  )
  const [autoCharge, setAutoCharge] = useState(false)
  const [paymentTokenId, setPaymentTokenId] = useState<string | null>(null)

  const { data: customersData } = useCustomers({ active: 'true', page_size: 9999 })
  const { data: contractsData } = useContracts(
    customerId ? { customer_id: customerId, status: 'active', page_size: 200 } : undefined
  )
  const contracts = customerId ? (contractsData?.contracts ?? []) : []
  const { data: customFieldsData } = useGlobalCustomFields(true) // solo activos
  const customFields = customFieldsData ?? []
  const { data: activeGateways = [] } = usePaymentGateways()
  const { data: customerTokens = [] } = useCustomerTokens(customerId || undefined)
  const create = useCreateRecurringInvoice()
  const update = useUpdateRecurringInvoice(subscription?.id ?? '')

  const isPending = create.isPending || update.isPending


  const addLine = () => setLines(prev => [...prev, { ...EMPTY_LINE }])
  const removeLine = (i: number) => setLines(prev => prev.filter((_, idx) => idx !== i))
  const updateLine = (i: number, field: keyof RecurringInvoiceLine, value: string) => {
    setLines(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l))
  }

  /** Las líneas con su posición adentro — ver el comentario de la tabla. */
  const filasDeLinea = useMemo<FilaDeLinea[]>(
    () => lines.map((l, idx) => ({ ...l, idx })),
    [lines],
  )

  const fillProductInLine = (index: number, product: ProductListItem) => {
    setLines(prev => prev.map((l, i) => i === index ? {
      ...l,
      sku: product.sku,
      name: product.name,
      unit: product.unit,
      unit_price: product.unit_price,
    } : l))
  }

  const estimatedTotal = lines.reduce((sum, l) => {
    const qty = parseFloat(l.quantity || '0')
    const price = parseFloat(l.unit_price || '0')
    return sum + qty * price
  }, 0)

  const handleSave = () => {
    if (!customerId || !frequency || !nextDate) return
    const validLines = lines.filter(l => l.sku && l.name && l.quantity && l.unit_price)
    if (!validLines.length) return

    setApiError(null)
    const payload = {
      customer: customerId,
      contract: contractId || null,
      document_type: docType,
      frequency,
      day_of_month: parseInt(dayOfMonth),
      next_generation_date: nextDate,
      generate_as: generateAs,
      notes,
      lines: validLines,
      personalizados,
      auto_charge: autoCharge,
      payment_token: autoCharge ? paymentTokenId : null,
    }

    if (isEdit) {
      update.mutate(payload as Parameters<typeof update.mutate>[0], {
        onSuccess: onClose,
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { error?: { message?: string } } } }
          setApiError(err.response?.data?.error?.message ?? 'Error al actualizar')
        },
      })
    } else {
      create.mutate(payload as Parameters<typeof create.mutate>[0], {
        onSuccess: onClose,
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { error?: { message?: string } } } }
          setApiError(err.response?.data?.error?.message ?? 'Error al crear')
        },
      })
    }
  }

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Editar Suscripción' : 'Nueva Suscripción'} size="2xl"
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={isPending || !customerId || !nextDate || !lines.some(l => l.sku && l.name)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isPending && <Loader2 size={14} className="animate-spin" />}
          {isEdit ? 'Guardar Cambios' : 'Crear Suscripción'}
        </button>
      </>}
    >
      {/* Body */}
      <div className="overflow-y-auto flex-1 space-y-4">
        {/* Cliente y tipo */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="subscriptionmodal-cliente" className="block text-xs font-medium text-content-muted mb-1">Cliente *</label>
            <select id="subscriptionmodal-cliente"
              value={customerId}
              onChange={e => { setCustomerId(e.target.value); setContractId('') }}
              disabled={isEdit}
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm text-content focus:ring-2 focus:ring-primary disabled:bg-surface-muted disabled:text-content-muted"
            >
              <option value="">Seleccionar cliente...</option>
              {customersData?.customers.map(c => (
                <option key={c.id} value={c.id}>{c.legal_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="subscriptionmodal-tipo-de-documento" className="block text-xs font-medium text-content-muted mb-1">Tipo de Documento *</label>
            <select id="subscriptionmodal-tipo-de-documento"
              value={docType}
              onChange={e => setDocType(e.target.value as 'FE' | 'FNA' | 'BE' | 'BNA')}
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm text-content focus:ring-2 focus:ring-primary"
            >
              <option value="FE">Factura Electrónica</option>
              <option value="FNA">Factura Exenta</option>
              <option value="BE">Boleta Electrónica</option>
              <option value="BNA">Boleta Exenta</option>
            </select>
          </div>
        </div>

        {/* Contrato (opcional) */}
        {customerId && contracts.length > 0 && (
          <div>
            <label htmlFor="subscriptionmodal-contrato-opcional" className="block text-xs font-medium text-content-muted mb-1">Contrato (opcional)</label>
            <select id="subscriptionmodal-contrato-opcional"
              value={contractId}
              onChange={e => setContractId(e.target.value)}
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm text-content focus:ring-2 focus:ring-primary"
            >
              <option value="">Sin contrato</option>
              {contracts.map(c => (
                <option key={c.id} value={c.id}>
                  {c.contract_number}{c.title ? ` — ${c.title}` : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-content-subtle mt-1">
              Si la ligas a un contrato, la suscripción hereda su vigencia y deja de generar al expirar.
            </p>
          </div>
        )}

        {/* Frecuencia y configuración */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label htmlFor="subscriptionmodal-frecuencia" className="block text-xs font-medium text-content-muted mb-1">Frecuencia *</label>
            <select id="subscriptionmodal-frecuencia"
              value={frequency}
              onChange={e => setFrequency(e.target.value as 'weekly' | 'monthly' | 'quarterly' | 'annual')}
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm text-content focus:ring-2 focus:ring-primary"
            >
              <option value="weekly">Semanal</option>
              <option value="monthly">Mensual</option>
              <option value="quarterly">Trimestral</option>
              <option value="annual">Anual</option>
            </select>
          </div>
          <div>
            <label htmlFor="subscriptionmodal-dia-del-mes" className="block text-xs font-medium text-content-muted mb-1">Día del mes</label>
            <input id="subscriptionmodal-dia-del-mes"
              type="number"
              value={dayOfMonth}
              onChange={e => setDayOfMonth(e.target.value)}
              min="1"
              max="31"
              disabled={frequency === 'weekly'}
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm text-content focus:ring-2 focus:ring-primary disabled:bg-surface-muted disabled:text-content-subtle"
            />
          </div>
          <div>
            <label htmlFor="subscriptionmodal-proxima-generacion" className="block text-xs font-medium text-content-muted mb-1">Próxima generación *</label>
            <DatePicker id="subscriptionmodal-proxima-generacion" value={nextDate} onChange={setNextDate} />
          </div>
          <div>
            <label htmlFor="subscriptionmodal-modo-de-generacion" className="block text-xs font-medium text-content-muted mb-1">Modo de generación</label>
            <select id="subscriptionmodal-modo-de-generacion"
              value={generateAs}
              onChange={e => setGenerateAs(e.target.value as 'draft' | 'auto_emit')}
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm text-content focus:ring-2 focus:ring-primary"
            >
              <option value="draft">Borrador</option>
              <option value="auto_emit">Emitir Automáticamente</option>
            </select>
          </div>
        </div>

        {/* Líneas */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-content-muted">Líneas de la Factura *</label>
            <span className="text-xs text-content-subtle font-medium">
              Total estimado: {formatMoneyForTenant(estimatedTotal)}
            </span>
          </div>

          <div className="border border-divider rounded-lg overflow-hidden">
            {/*
              Las filas llevan su POSICIÓN adentro: `updateLine` y `removeLine`
              reciben el índice del array y el `render` recibe el de la fila ya
              ordenada. Con el índice en el dato los dos coinciden siempre.
              🔴 Aun así ninguna columna lleva `sortable`: el orden de las
              líneas es del documento, no una vista.
            */}
            <DataTable<FilaDeLinea>
              columns={[
                { key: 'sku', header: 'SKU', width: 112,
                  render: f => (
                    <ProductAutocomplete aria-label="SKU"
                      field="sku"
                      value={f.sku}
                      onChange={val => updateLine(f.idx, 'sku', val)}
                      onSelectProduct={(prod) => { fillProductInLine(f.idx, prod) }}
                      placeholder="SKU"
                      className="w-24 border border-divider rounded px-2 py-1 text-xs focus:ring-1 focus:ring-primary"
                    />
                  ) },
                { key: 'name', header: 'Descripción',
                  render: f => (
                    <ProductAutocomplete aria-label="Descripción"
                      field="name"
                      value={f.name}
                      onChange={val => updateLine(f.idx, 'name', val)}
                      onSelectProduct={(prod) => { fillProductInLine(f.idx, prod) }}
                      placeholder="Descripción"
                      className="w-full border border-divider rounded px-2 py-1 text-xs focus:ring-1 focus:ring-primary"
                    />
                  ) },
                { key: 'unit', header: 'Unidad', width: 80,
                  render: f => (
                    <select
                      value={f.unit}
                      onChange={e => updateLine(f.idx, 'unit', e.target.value)}
                      aria-label={`Unidad de la línea ${f.idx + 1}`}
                      className="w-16 border border-divider rounded px-1 py-1 text-xs focus:ring-1 focus:ring-primary"
                    >
                      <option value="UN">UN</option>
                      <option value="KG">KG</option>
                      <option value="MT">MT</option>
                      <option value="LT">LT</option>
                      <option value="HR">HR</option>
                      <option value="MO">MO</option>
                    </select>
                  ) },
                { key: 'quantity', header: 'Cantidad', numeric: true,
                  render: f => (
                    <NumericInput
                      value={f.quantity}
                      onChange={v => updateLine(f.idx, 'quantity', v)}
                      locale={locale}
                      decimals={3}
                      min={0}
                      aria-label={`Cantidad de la línea ${f.idx + 1}`}
                      className="w-20 border border-divider rounded px-2 py-1 text-xs text-right focus:ring-1 focus:ring-primary"
                    />
                  ) },
                { key: 'unit_price', header: 'Precio Unit.', numeric: true,
                  render: f => (
                    <NumericInput
                      value={f.unit_price}
                      onChange={v => updateLine(f.idx, 'unit_price', v)}
                      locale={locale}
                      decimals={3}
                      min={0}
                      aria-label={`Precio unitario de la línea ${f.idx + 1}`}
                      className="w-28 border border-divider rounded px-2 py-1 text-xs text-right focus:ring-1 focus:ring-primary"
                    />
                  ) },
                { key: 'quitar', header: '', width: 32,
                  render: f => (
                    <div className="text-center">
                      {lines.length > 1 && (
                        <button onClick={() => removeLine(f.idx)}
                          title="Quitar línea"
                          className="p-1 rounded text-content-subtle hover:text-danger-solid hover:bg-danger-surface transition-colors">
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  ) },
              ]}
              rows={filasDeLinea}
              rowKey={f => f.idx}
              surface={false}
              caption="Líneas de la suscripción"
            />
            <div className="px-3 py-2 border-t border-divider">
              <button onClick={addLine}
                className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary-hover">
                <Plus size={12} />Agregar línea
              </button>
            </div>
          </div>
        </div>

        {/* Notas */}
        <div>
          <label htmlFor="subscriptionmodal-notas-internas" className="block text-xs font-medium text-content-muted mb-1">Notas internas</label>
          <textarea id="subscriptionmodal-notas-internas"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            placeholder="Observaciones opcionales..."
            className="w-full border border-divider rounded-lg px-3 py-2 text-sm text-content focus:ring-2 focus:ring-primary resize-none"
          />
        </div>

        {/* Campos Adicionales */}
        {customFields.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-content-muted mb-2">Campos Adicionales</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {customFields.map(field => (
                <div key={field.nombre}>
                  <label className="block text-xs text-content-muted mb-1">
                    {field.nombre}{field.is_required && ' *'}
                  </label>
                  {field.tipo === 'numero' ? (
                    <input aria-label={field.nombre}
                      type="number"
                      value={personalizados[field.nombre] ?? ''}
                      onChange={e => setPersonalizados(prev => ({ ...prev, [field.nombre]: e.target.value }))}
                      placeholder={field.nombre}
                      className="w-full border border-divider rounded-lg px-3 py-2 text-sm text-content focus:ring-2 focus:ring-primary"
                    />
                  ) : (
                    <input aria-label={field.nombre}
                      type="text"
                      value={personalizados[field.nombre] ?? ''}
                      onChange={e => setPersonalizados(prev => ({ ...prev, [field.nombre]: e.target.value }))}
                      placeholder={field.nombre}
                      className="w-full border border-divider rounded-lg px-3 py-2 text-sm text-content focus:ring-2 focus:ring-primary"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cargo Automático */}
        {activeGateways.some(g => g.is_active && g.has_credentials) && (
          <div className="border border-divider rounded-xl p-4 bg-glass-3">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs font-semibold text-content">Cargo Automático</p>
                <p className="text-xs text-content-subtle mt-0.5">
                  Cobra automáticamente al cliente al generar cada factura.
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setAutoCharge(p => !p); setPaymentTokenId(null) }}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                  autoCharge ? 'bg-primary' : 'bg-surface-muted'
                }`}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-surface shadow transition-transform ${
                  autoCharge ? 'translate-x-4.5' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
            {autoCharge && (
              <div>
                <label className="block text-xs font-medium text-content-muted mb-1">
                  Tarjeta guardada del cliente
                </label>
                {customerTokens.length === 0 ? (
                  <p className="text-xs text-content-subtle italic">
                    Este cliente no tiene tarjetas guardadas. El cargo automático se omitirá hasta que se registre una.
                  </p>
                ) : (
                  <select aria-label="Seleccionar tarjeta"
                    value={paymentTokenId ?? ''}
                    onChange={e => setPaymentTokenId(e.target.value || null)}
                    className="w-full border border-divider rounded-lg px-3 py-2 text-sm text-content focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Seleccionar tarjeta...</option>
                    {customerTokens.map(token => (
                      <option key={token.id} value={token.id}>
                        {token.card_brand} •••• {token.card_last4}
                        {token.card_exp_month && token.card_exp_year
                          ? ` (exp. ${token.card_exp_month}/${token.card_exp_year})`
                          : ''}
                        {token.is_default ? ' — por defecto' : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>
        )}

        {apiError && (
          <div className="bg-danger-surface border border-danger-border rounded-lg px-3 py-2 text-sm text-danger-content">
            {apiError}
          </div>
        )}
      </div>

      {/* Footer */}
    </Modal>
  )
}
